import React, { useState, ReactNode, useRef, useEffect, useCallback, useMemo } from 'react'
import Button from 'app/app/(client)/components/atoms/Button/Button'
import Icon from 'app/app/(client)/components/atoms/Icon/Icon'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import {
  calculateOptimalPosition,
  calculateHorizontalAlignment,
  ElementPosition,
  HorizontalAlignment
} from 'app/app/common/common-utils'

interface ActionButtonProps {
  id: string
  children: ReactNode
  className?: string
  iconName?: string
  variant?: 'primary' | 'secondary' | 'tertiary' | 'danger'
  size?: 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge'
  text?: string
  disabled?: boolean
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'auto'
}

const PADDING = 10
const DEBOUNCE_DELAY = 500

const ActionButton: React.FC<ActionButtonProps> = ({
  id,
  iconName,
  text,
  children,
  className = '',
  variant = 'primary',
  disabled = false,
  size = 'medium',
  position = 'bottom-left'
}) => {
  const [open, setOpen] = useState(false)
  const [calculatedPosition, setCalculatedPosition] = useState<ElementPosition>('bottom')
  const [horizontalAlign, setHorizontalAlign] = useState<HorizontalAlignment>('left')
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  const calculatePosition = useCallback(() => {
    if (position !== 'auto' || !containerRef.current || !dropdownRef.current) {
      return
    }

    const newPosition = calculateOptimalPosition(containerRef.current, dropdownRef.current, PADDING)
    const newHorizontalAlign = calculateHorizontalAlignment(containerRef.current, dropdownRef.current)
    setCalculatedPosition((prev: ElementPosition) => (prev !== newPosition ? newPosition : prev))
    setHorizontalAlign((prev: HorizontalAlignment) => (prev !== newHorizontalAlign ? newHorizontalAlign : prev))
  }, [position])

  const debouncedCalculatePosition = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(calculatePosition, DEBOUNCE_DELAY)
  }, [calculatePosition])

  React.useEffect(() => {
    if (!open) {
      return
    }
    const handle = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(`#${id}-action-btn-wrapper`)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [open, id])

  useEffect(() => {
    if (!open || position !== 'auto') {
      return
    }

    calculatePosition()

    window.addEventListener('resize', debouncedCalculatePosition)
    window.addEventListener('scroll', debouncedCalculatePosition, true)

    return () => {
      window.removeEventListener('resize', debouncedCalculatePosition)
      window.removeEventListener('scroll', debouncedCalculatePosition, true)
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [open, position, calculatePosition, debouncedCalculatePosition])

  const getDropdownPositionClass = useMemo(() => {
    if (position === 'auto') {
      const horizontalClass = horizontalAlign === 'right' ? 'right-0' : 'left-0'
      switch (calculatedPosition) {
        case 'top':
          return `${horizontalClass} bottom-full mb-1`
        case 'bottom':
          return `${horizontalClass} mt-1`
        case 'left':
          return 'right-full mr-1 top-0'
        case 'right':
          return 'left-full ml-1 top-0'
        default:
          return `${horizontalClass} mt-1`
      }
    }

    switch (position) {
      case 'top-left':
        return 'left-0 bottom-full mb-1'
      case 'top-right':
        return 'right-0 bottom-full mb-1'
      case 'bottom-left':
        return 'left-0 mt-1'
      case 'bottom-right':
        return 'right-0 mt-1'
      default:
        return 'left-0 mt-1'
    }
  }, [position, calculatedPosition, horizontalAlign])

  const dropdownPositionClass = getDropdownPositionClass

  let displayIconName = iconName
  if (iconName === 'icon-view-list') {
    displayIconName = open ? 'icon-close-cross' : 'icon-view-list'
  }

  // Helper function for wrapper class assignment
  const getWrapperClass = () =>
    className.match('fab__button') ? 'fab' : iconName === 'icon-view-list' ? '' : 'relative inline-block'

  return (
    <div ref={containerRef} id={`${id}-action-btn-wrapper`} className={getWrapperClass()}>
      <Button
        id={`${id}-action-btn`}
        className={`${className} btn`}
        onClick={() => setOpen(o => !o)}
        variant={variant}
        size={size}
        disabled={disabled}
        aria-label={displayIconName ? displayIconName : text}
      >
        <Icon name={displayIconName || ''} className={`${size} ${open ? 'opened' : ''}`} />
        <Text as="span" className={`${size}`}>
          {text}
        </Text>
      </Button>
      {open && (
        <div
          ref={dropdownRef}
          id={`${id}-action-btn-dropdown`}
          className={
            className.match('fab-btn')
              ? 'fab-dropdown'
              : iconName === 'icon-burger'
                ? 'bg-white border-[0.5px] border-white w-[100vw] h-[92vh] absolute z-50 top-16 right-0 left-0'
                : `absolute z-50 bg-white border-[0.5px] border-white rounded-2xl shadow-lg w-[100%] min-w-[232px] py-2 px-3 ${dropdownPositionClass}`
          }
        >
          {children}
        </div>
      )}
    </div>
  )
}

export default ActionButton
